import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input'
import { User, Building2, Mail, ArrowRight, Hash } from 'lucide-react'
import PhoneInputField, { SUPPORTED_COUNTRIES } from './PhoneInputField'

const clientSchema = z.object({
  firstName: z.string()
    .min(2, "Prénom requis (minimum 2 caractères)")
    .max(50, "Maximum 50 caractères"),
  lastName: z.string()
    .min(2, "Nom requis (minimum 2 caractères)")
    .max(50, "Maximum 50 caractères"),
  email: z.string()
    .email("Email invalide")
    .min(1, "Email requis"),
  company: z.string()
    .min(2, "Nom d'entreprise requis")
    .max(100, "Maximum 100 caractères"),
  clientId: z.string()
    .min(2, "N° Dossier requis (minimum 2 caractères)")
    .max(50, "Maximum 50 caractères")
    .regex(/^[a-zA-Z0-9_./-]+$/, "Le N° Dossier accepte uniquement lettres, chiffres, tirets, soulignés, points et barres obliques"),
  phone: z.string()
    .min(1, "Numéro de téléphone requis")
    .refine(
      (val) => isValidPhoneNumber(val),
      "Numéro de téléphone invalide"
    )
    .refine(
      (val) => {
        try {
          const parsed = parsePhoneNumber(val)
          return parsed && SUPPORTED_COUNTRIES.includes(parsed.country)
        } catch {
          return false
        }
      },
      "Seuls les numéros FR, BE, CH, LU, MC, AD, IT, ES et PT sont acceptés"
    ),
  consultantEmail: z.string()
    .min(1, "Email du consultant requis")
    .email("Email consultant invalide")
})

function ClientInfoForm({ onSubmit, onResume, defaultValues = null, hasProgress = false, savedStep = null, savedThemeIndex = 0, hasScores = false }) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(clientSchema),
    defaultValues: defaultValues || {}
  })

  const onFormSubmit = (data) => {
    const formattedData = {
      ...data,
      assessmentDate: new Date().toISOString(),
      phone: (data.phone || '').replace(/\s+/g, '') // E.164 cleanup
    }
    onSubmit(formattedData, false) // false = start fresh
  }
  
  const onResumeClick = () => {
    if (onResume) {
      onResume()
    }
  }
  
  // Check if user is in assessment phase (not just client info saved)
  // We should show resume if:
  // 1. There's progress saved (hasProgress)
  // 2. User was in assessment step (savedStep === 'assessment') OR has scores saved
  // 3. We have a valid theme index (>= 0)
  const hasValidThemeIndex = typeof savedThemeIndex === 'number' && savedThemeIndex >= 0
  const isInAssessment = savedStep === 'assessment'
  
  // More lenient check: show if we have scores saved, even if step got corrupted
  const hasAssessmentData = hasScores || (hasProgress && hasValidThemeIndex)
  const canResume = (hasProgress && isInAssessment && hasValidThemeIndex) || hasAssessmentData

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8 max-w-2xl mx-auto">
      {/* Resume Assessment Banner */}
      {canResume && (
        <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-lg p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <svg className="h-6 w-6 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-semibold text-blue-900">
                  Évaluation en cours détectée
                </h3>
              </div>
              <p className="text-sm text-blue-800 mb-3">
                Vous avez une évaluation en cours pour <strong>{defaultValues?.company || 'votre entreprise'}</strong>
                {savedThemeIndex > 0 && ` (Thème ${savedThemeIndex + 1})`}.
              </p>
              <button
                onClick={onResumeClick}
                type="button"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Reprendre l'évaluation
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-cedec-blue mb-2">
          {canResume ? 'Nouvelle évaluation' : 'Informations client'}
        </h2>
        <p className="text-gray-600">
          {canResume 
            ? 'Ou démarrez une nouvelle évaluation en renseignant vos coordonnées ci-dessous'
            : 'Merci de renseigner vos coordonnées pour recevoir votre rapport d\'évaluation'
          }
        </p>
      </div>

      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
        {/* First Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <User className="inline w-4 h-4 mr-2" />
            Prénom *
          </label>
          <input
            type="text"
            {...register('firstName')}
            className={`
              w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-cedec-blue focus:border-transparent
              ${errors.firstName ? 'border-red-500' : 'border-gray-300'}
            `}
            placeholder="Votre prénom"
          />
          {errors.firstName && (
            <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
          )}
        </div>

        {/* Last Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <User className="inline w-4 h-4 mr-2" />
            Nom *
          </label>
          <input
            type="text"
            {...register('lastName')}
            className={`
              w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-cedec-blue focus:border-transparent
              ${errors.lastName ? 'border-red-500' : 'border-gray-300'}
            `}
            placeholder="Votre nom"
          />
          {errors.lastName && (
            <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
          )}
        </div>

        {/* Company */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Building2 className="inline w-4 h-4 mr-2" />
            Entreprise *
          </label>
          <input
            type="text"
            {...register('company')}
            className={`
              w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-cedec-blue focus:border-transparent
              ${errors.company ? 'border-red-500' : 'border-gray-300'}
            `}
            placeholder="Nom de votre entreprise"
          />
          {errors.company && (
            <p className="mt-1 text-sm text-red-600">{errors.company.message}</p>
          )}
        </div>

      

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Mail className="inline w-4 h-4 mr-2" />
            Email *
          </label>
          <input
            type="email"
            {...register('email')}
            className={`
              w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-cedec-blue focus:border-transparent
              ${errors.email ? 'border-red-500' : 'border-gray-300'}
            `}
            placeholder="votre.email@entreprise.com"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        {/* Phone */}
        <Controller
          name="phone"
          control={control}
          defaultValue=""
          render={({ field: { onChange, onBlur, value } }) => (
            <PhoneInputField
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={errors.phone?.message}
              defaultCountry="FR"
            />
          )}
        />

          {/* Client ID / N° Dossier */}
          <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Hash className="inline w-4 h-4 mr-2" />
            N° Dossier *
          </label>
          <input
            type="text"
            {...register('clientId')}
            className={`
              w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-cedec-blue focus:border-transparent
              ${errors.clientId ? 'border-red-500' : 'border-gray-300'}
            `}
            placeholder="Ex: ACME-2025-001"
          />
          {errors.clientId && (
            <p className="mt-1 text-sm text-red-600">{errors.clientId.message}</p>
          )}
          <p className="mt-1 text-sm text-gray-500">
          N° Dossier : Identifiant unique du client dans votre système
          </p>
        </div>

        {/* Consultant Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Mail className="inline w-4 h-4 mr-2" />
            Email du consultant CEDEC *
          </label>
          <input
            type="email"
            {...register('consultantEmail')}
            className={`
              w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-cedec-blue focus:border-transparent
              ${errors.consultantEmail ? 'border-red-500' : 'border-gray-300'}
            `}
            placeholder="consultant@cedec.fr"
          />
          {errors.consultantEmail && (
            <p className="mt-1 text-sm text-red-600">{errors.consultantEmail.message}</p>
          )}
          <p className="mt-1 text-sm text-amber-600 font-medium">
            ⚠️ Cette évaluation doit être réalisée avec l'accompagnement d'un consultant CEDEC pour garantir la qualité des réponses
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Le rapport sera envoyé à vous et à votre consultant
          </p>
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`
              w-full flex items-center justify-center gap-2 py-3 px-6 rounded-lg font-medium text-white
              ${isSubmitting 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-cedec-blue hover:bg-blue-700 focus:ring-2 focus:ring-cedec-blue focus:ring-offset-2'
              }
              transition-colors duration-200
            `}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Traitement...
              </>
            ) : (
              <>
                Commencer l'évaluation
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </form>

      <div className="mt-6 text-center text-sm text-gray-500">
        <p>
          📧 Votre rapport sera envoyé à l'adresse email renseignée
        </p>
        <p className="mt-1">
          🔒 Vos données sont sécurisées et conformes au RGPD
        </p>
      </div>
    </div>
  )
}

export default ClientInfoForm
